// Pricing reference for one line — ONE compact horizontal line per row, not
// a stacked two-column block. Real-use bug fix: the previous, taller design
// (a heading + tier list/bullet list in one column, a heading + quote list
// in a second column) meant three priced lines filled the whole 1366x768
// viewport and the table stopped reading as a table (admin/CLAUDE.md:
// "Desktop first at 1366x768... Verify at that exact size"). This version
// is deliberately just: [tier · list · floor] [meter] [last 3 quotes,
// inline] [other tiers toggle, collapsed by default].
//
// Cost boundary in the UI (root CLAUDE.md): the meter's domain is
// `[floorPrice, listPrice]` ONLY, computed entirely from
// GET /catalog/products (which never selects cost — see
// repositories/productRepository.js#getProductsForSales). Nothing here
// ever reads or derives from a cost/margin value; none exists in any
// response this component touches.
//
// Tier is now deterministic, not ambiguous: `organizationTier` comes from
// `requests.organization_tier` (backend/src/repositories/
// requestRepository.js's join onto `organizations.tier`, added specifically
// so this component could stop guessing). The ONE product_prices row whose
// `tier` matches the request's own organization is "the" tier for this
// customer — shown as the single reference line, always with the position
// meter now that the domain is unambiguous. Every OTHER tier this product
// happens to have priced is still available, just not shown by default —
// collapsed behind a small toggle (closed on every mount, same "no
// persisted UI state" discipline as PasswordInput's visibility).

import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext.jsx';
import { useRecentQuotes } from '../hooks/useRecentQuotes.js';
import { formatMoney } from 'shared/money.js';
import { formatDate } from '../utils/formatDate.js';
import styles from './PriceReference.module.css';

function PriceMeter({ floorPrice, listPrice, currentValue }) {
  const floor = Number(floorPrice);
  const list = Number(listPrice);
  const current = Number(currentValue);
  if (!Number.isFinite(floor) || !Number.isFinite(list) || list <= floor) return null;

  const hasValue = currentValue !== '' && currentValue !== undefined && Number.isFinite(current);
  const percent = hasValue
    ? Math.min(100, Math.max(0, ((current - floor) / (list - floor)) * 100))
    : null;
  const belowFloor = hasValue && current < floor;

  return (
    <span className={styles.meterTrack} aria-hidden="true">
      {hasValue && (
        <span
          className={`${styles.meterMarker} ${belowFloor ? styles.meterMarkerAmber : ''}`}
          style={{ left: `${percent}%` }}
        />
      )}
    </span>
  );
}

export default function PriceReference({
  productId,
  catalogEntry,
  currentValue,
  quotationId,
  organizationTier,
}) {
  const { t, lang } = useLanguage();
  const { data: quotes, isLoading: quotesLoading } = useRecentQuotes(productId, quotationId);
  const [othersOpen, setOthersOpen] = useState(false);

  const tiers = catalogEntry ? catalogEntry.tiers : [];
  const primaryTier = organizationTier
    ? tiers.find((tierRow) => tierRow.tier === organizationTier)
    : undefined;
  const otherTiers = tiers.filter((tierRow) => tierRow !== primaryTier);

  return (
    <div className={styles.wrapper}>
      <div className={styles.line}>
        {tiers.length === 0 && (
          <span className={styles.muted}>{t('workspace.reference.none')}</span>
        )}

        {tiers.length > 0 && primaryTier && (
          <span className={`${styles.tierText} tabularNums`}>
            {t(`workspace.reference.tier.${primaryTier.tier}`)} · {t('workspace.reference.list')}{' '}
            {formatMoney(primaryTier.listPrice)} · {t('workspace.reference.floor')}{' '}
            {formatMoney(primaryTier.floorPrice)}
          </span>
        )}
        {tiers.length > 0 && primaryTier && (
          <PriceMeter
            floorPrice={primaryTier.floorPrice}
            listPrice={primaryTier.listPrice}
            currentValue={currentValue}
          />
        )}

        {tiers.length > 0 && !primaryTier && (
          <span className={styles.muted}>{t('workspace.reference.noTierPrice')}</span>
        )}

        <span className={styles.divider} aria-hidden="true">
          ·
        </span>

        {quotesLoading && <span className={styles.muted}>{t('state.loading')}</span>}
        {!quotesLoading && (!quotes || quotes.length === 0) && (
          <span className={styles.muted}>{t('workspace.recentQuotes.emptyShort')}</span>
        )}
        {!quotesLoading && quotes && quotes.length > 0 && (
          <span
            className="tabularNums"
            title={quotes
              .map((q) =>
                t('workspace.recentQuotes.row', {
                  price: formatMoney(q.unit_price),
                  organization: q.organization_name,
                  date: formatDate(q.quoted_at, lang) || '—',
                })
              )
              .join(' | ')}
          >
            {t('workspace.recentQuotes.labelShort')}{' '}
            {quotes
              .map((q) =>
                t('workspace.recentQuotes.compactItem', {
                  price: formatMoney(q.unit_price),
                  date: formatDate(q.quoted_at, lang) || '—',
                })
              )
              .join(' · ')}
          </span>
        )}

        {otherTiers.length > 0 && (
          <button
            type="button"
            className={styles.othersToggle}
            onClick={() => setOthersOpen((prev) => !prev)}
            aria-expanded={othersOpen}
          >
            {othersOpen ? (
              <ChevronUp size={14} aria-hidden="true" />
            ) : (
              <ChevronDown size={14} aria-hidden="true" />
            )}
            {t('workspace.reference.otherTiers', { count: otherTiers.length })}
          </button>
        )}
      </div>

      {othersOpen && otherTiers.length > 0 && (
        <div className={styles.othersPanel}>
          {otherTiers.map((tierRow) => (
            <span key={tierRow.tier} className="tabularNums">
              {t(`workspace.reference.tier.${tierRow.tier}`)}: {t('workspace.reference.list')}{' '}
              {formatMoney(tierRow.listPrice)} · {t('workspace.reference.floor')}{' '}
              {formatMoney(tierRow.floorPrice)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
